export type LifecycleStage =
  | 'INACTIVE'
  | 'REACHED_LIMIT'
  | 'TRIED_LOCKED'
  | 'PREMIUM'
  | 'HEAVY_USAGE'
  | 'ACTIVE_USER'
  | 'FIRST_DOCUMENT'
  | 'NEW_ACCOUNT';

export interface LifecycleStageInfo {
  stage: LifecycleStage;
  label: string;
  color: string;
  bgColor: string;
  reached: boolean;
  detail?: string;
  date?: string;
}

const INACTIVE_DAYS = 14;
const HEAVY_USAGE_TOKENS = 100000;

interface LeadLifecycleData {
  externalCreatedAt?: string | null;
  externalDocsGenerated?: number | null;
  externalDocsUsed?: number | null;
  externalDocsLimit?: number | null;
  externalFirstDocAt?: string | null;
  externalLastActivityAt?: string | null;
  externalTriedLockedFeature?: boolean | null;
  externalTotalTokens?: number | null;
  stageType?: string | null;
}

function daysSince(dateStr: string): number {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function computeLifecycleStages(lead: LeadLifecycleData): LifecycleStageInfo[] {
  const docs = lead.externalDocsGenerated ?? lead.externalDocsUsed ?? 0;
  const limit = lead.externalDocsLimit ?? 0;
  const tokens = lead.externalTotalTokens ?? 0;

  const stages: LifecycleStageInfo[] = [
    {
      stage: 'NEW_ACCOUNT',
      label: 'Conta criada',
      color: 'text-sky-600',
      bgColor: 'bg-sky-100 text-sky-700',
      reached: !!lead.externalCreatedAt,
      date: lead.externalCreatedAt ? formatDate(lead.externalCreatedAt) : undefined,
    },
    {
      stage: 'FIRST_DOCUMENT',
      label: 'Primeiro documento',
      color: 'text-green-600',
      bgColor: 'bg-green-100 text-green-700',
      reached: !!lead.externalFirstDocAt,
      date: lead.externalFirstDocAt ? formatDate(lead.externalFirstDocAt) : undefined,
    },
    {
      stage: 'PREMIUM',
      label: 'Assinante',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100 text-emerald-700',
      reached: lead.stageType === 'WON',
    },
    {
      stage: 'ACTIVE_USER',
      label: 'Usuário ativo',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 text-blue-700',
      reached: docs >= 3,
      detail: limit > 0 ? `${docs}/${limit} documentos` : `${docs} documentos`,
    },
    {
      stage: 'TRIED_LOCKED',
      label: 'Tentou premium',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100 text-orange-700',
      reached: !!lead.externalTriedLockedFeature,
    },
    {
      stage: 'HEAVY_USAGE',
      label: 'Uso intenso',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100 text-purple-700',
      reached: tokens >= HEAVY_USAGE_TOKENS,
      detail: tokens > 0 ? `${Math.round(tokens / 1000)}k tokens` : undefined,
    },
    {
      stage: 'REACHED_LIMIT',
      label: 'Atingiu limite',
      color: 'text-red-600',
      bgColor: 'bg-red-100 text-red-700',
      reached: limit > 0 && docs >= limit,
      detail: limit > 0 ? `${docs}/${limit}` : undefined,
    },
    {
      stage: 'INACTIVE',
      label: 'Inativo',
      color: 'text-gray-500',
      bgColor: 'bg-gray-100 text-gray-600',
      reached:
        !!lead.externalLastActivityAt && daysSince(lead.externalLastActivityAt) > INACTIVE_DAYS,
      detail: lead.externalLastActivityAt
        ? `Último acesso: ${daysSince(lead.externalLastActivityAt)} dias atrás`
        : undefined,
    },
  ];

  return stages;
}

const BADGE_PRIORITY: LifecycleStage[] = [
  'INACTIVE',
  'REACHED_LIMIT',
  'TRIED_LOCKED',
  'PREMIUM',
  'HEAVY_USAGE',
  'ACTIVE_USER',
  'FIRST_DOCUMENT',
  'NEW_ACCOUNT',
];

export function getTopLifecycleStage(lead: LeadLifecycleData): LifecycleStageInfo | null {
  const stages = computeLifecycleStages(lead);
  for (const stageKey of BADGE_PRIORITY) {
    const stage = stages.find((s) => s.stage === stageKey && s.reached);
    if (stage) {
      return stage;
    }
  }
  return null;
}
